import { Injectable } from '@angular/core';
import { ApiHttpClient, OperationCanceledException, BroadcastService } from '../modules/tcm-core.module';
import { MenuQuantityItemEnum, MenuQuantityItemViewModel, MenuWorkQueueSubMenuViewModel } from '../models/menu';
import { Subject } from 'rxjs/internal/Subject';
import { merge } from 'rxjs/internal/observable/merge';
import { Observable } from 'rxjs/internal/Observable';
import { BroadCastEvents } from '../common/broad-cast-events.enum';
import { map } from 'rxjs/operators';
import { SingletonCheckHelper } from '../helpers/singleton-check.helper';
import { UserClinicService } from './user-clinic.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class MenuQuantitiesService {

  quantifiers$: Observable<MenuQuantityItemViewModel[]>

  private _quantifiersSubject: Subject<MenuQuantityItemViewModel[]>;

  workQueueQuantifiers$: Observable<MenuWorkQueueSubMenuViewModel[]>

  private _workQueueQuantifiersSubject: Subject<MenuWorkQueueSubMenuViewModel[]>;

  workQueueSubMenu$: Observable<MenuWorkQueueSubMenuViewModel[]>

  private _workQueueSubMenuSubject: Subject<MenuWorkQueueSubMenuViewModel[]>;

  private itemUpdateDates: any = {};

  private quantityMinUpdatePeriodSeconds: number = 1;

  private updatePromise: Promise<any>;
  private updateSubMenuPromise: Promise<any>;

  private menu = {
    scheduling: {
      scheduler: MenuQuantityItemEnum.Scheduler,
      waitList: MenuQuantityItemEnum.WaitList,
      appointments: MenuQuantityItemEnum.Appointments
    }
  };

  constructor(
    protected broadcastService: BroadcastService,
    private readonly userClinicService: UserClinicService,
    private readonly httpClient: ApiHttpClient,
    private authService: AuthService) {

    SingletonCheckHelper.ensureSingleton(this);

    this._quantifiersSubject = new Subject<MenuQuantityItemViewModel[]>();
    this.quantifiers$ = this._quantifiersSubject.asObservable();
    this._workQueueQuantifiersSubject = new Subject<MenuWorkQueueSubMenuViewModel[]>();
    this.workQueueQuantifiers$ = this._workQueueQuantifiersSubject.asObservable();
    this.updatePromise = Promise.resolve();

    this._workQueueSubMenuSubject = new Subject<MenuWorkQueueSubMenuViewModel[]>();
    this.workQueueSubMenu$ = this._workQueueSubMenuSubject.asObservable();
    this.updateSubMenuPromise = Promise.resolve();

    const menuItemObserver = this.getMenuItemObserver();
    const getWorkQueueItemObserver = this.getWorkQueueItemObserver();
    const subMenuObserver = this.getSubMenuItemObserver();
    menuItemObserver.subscribe(x => {
      this.updateQuantities(x);
    });

    subMenuObserver.subscribe(x => {
      this.updateWorkQueueSubMenu();
    });

    getWorkQueueItemObserver.subscribe(x=>{
      this.updateWorkQueueQuantities(x);
    })
  }

  updateQuantities(items: MenuQuantityItemEnum[] | MenuQuantityItemEnum) {
    this.updatePromise = this.updatePromise.then(() => this.updateQuantitiesAsync(items));
  }
  updateWorkQueueQuantities(items: MenuQuantityItemEnum[] | MenuQuantityItemEnum) {
    this.updatePromise = this.updatePromise.then(() => this.updateWorkQueueQuantitiesAsync(items));
  }

  updateWorkQueueSubMenu() {
    this.updateSubMenuPromise = this.updateSubMenuPromise.then(() => this.updateWorkQueueSubMenuAsync());
  }

  private getMenuItemObserver() {
    const waitListObserver = this.broadcastService.getEvents(BroadCastEvents.WaitListChanged).pipe(map(z => MenuQuantityItemEnum.WaitList));
    const appRequestObserver = this.broadcastService.getEvents(BroadCastEvents.AppointmentRequestChanged).pipe(map(z => MenuQuantityItemEnum.AppointmentRequests));
    const recallsObserver = this.broadcastService.getEvents(BroadCastEvents.RecallChanged).pipe(map(z => MenuQuantityItemEnum.Recalls));
    const userClinicObserver = this.broadcastService.getEvents(BroadCastEvents.UserClinicChanged).pipe(map(z => [MenuQuantityItemEnum.WaitList, MenuQuantityItemEnum.AppointmentRequests, MenuQuantityItemEnum.Recalls, MenuQuantityItemEnum.Appointments]));
    const appointmentObserver = this.broadcastService.getEvents(BroadCastEvents.AppointmentTouched).pipe(map(z => MenuQuantityItemEnum.Appointments));

    return merge(waitListObserver, appRequestObserver, recallsObserver, userClinicObserver, appointmentObserver);
  }

  private getWorkQueueItemObserver(){
    const workQueueMenuTouched=this.broadcastService.getEvents(BroadCastEvents.WorkQueueTouched).pipe(map(z => MenuQuantityItemEnum.WorkQueue));
    return merge(workQueueMenuTouched);
  }

  private getSubMenuItemObserver() {
    const workQueueObserver = this.broadcastService.getEvents(BroadCastEvents.NoteChanged).pipe(map(z => MenuQuantityItemEnum.WorkQueue));
    return workQueueObserver;
  }

  private updateQuantitiesAsync(items: MenuQuantityItemEnum[] | MenuQuantityItemEnum) {
    return new Promise(async (resolve, reject) => {
      try {
        await this.updateQuantitiesInternalAsync(items);
      }
      finally {
        resolve(null);
      }
    });
  }
  private updateWorkQueueQuantitiesAsync(items: MenuQuantityItemEnum[] | MenuQuantityItemEnum) {
    return new Promise(async (resolve, reject) => {
      try {
        await this.updateWorkQueueQuantitiesInternalAsync(items);
      }
      finally {
        resolve(null);
      }
    });
  }

  private updateWorkQueueSubMenuAsync() {
    return new Promise(async (resolve, reject) => {
      try {
        await this.updateWorkQueueSubMenuInternalAsync();
      }
      finally {
        resolve(null);
      }
    });
  }

  private async updateWorkQueueSubMenuInternalAsync() {
    const viewModels: MenuWorkQueueSubMenuViewModel[] = [];

    try {

      const additionalNodes = await this.getWorkQueueSubMenuAsync();

      viewModels.push(...additionalNodes);

      this._workQueueSubMenuSubject.next(viewModels);
    }
    catch (e) {
      if (e instanceof OperationCanceledException)
        return;

      console.error(`Failed to update menu quantities: ${JSON.stringify(e)}`);
    }
  }

  private async updateQuantitiesInternalAsync(items: MenuQuantityItemEnum[] | MenuQuantityItemEnum) {
    if (!items)
      return;

    if (!(items instanceof Array)) {
      items = [items]; // this.getGroupMenuQuantityItemsByMenuQuantityItem(items);
    }

    if (!items.length)
      return;

    const currentTime = new Date().getTime();

    const viewModels: MenuQuantityItemViewModel[] = [];
    const itemsForRequest: MenuQuantityItemEnum[] = [];
    for (const item of items) {

      const lastMenuItemData: LastMenuItemData = this.itemUpdateDates[item];

      if (!lastMenuItemData || (currentTime - lastMenuItemData.lastUpdatedDate.getTime()) / 1000 >= this.quantityMinUpdatePeriodSeconds) {
        itemsForRequest.push(item);
      }
      else {
        viewModels.push(lastMenuItemData.viewModel);
      }
    }

    if (!itemsForRequest.length)
      return;

    try {
      const quantities = await this.getQuantitiesAsync(itemsForRequest);
      const currentDate = new Date();

      for (const quan of quantities) {
        this.itemUpdateDates[quan.menuQuantityItem] = {
          lastUpdatedDate: currentDate,
          viewModel: quan
        } as LastMenuItemData;
      }

      viewModels.push(...quantities);

      this._quantifiersSubject.next(viewModels);
    }
    catch (e) {
      if (e instanceof OperationCanceledException)
        return;

      console.error(`Failed to update menu quantities: ${JSON.stringify(e)}`);
    }
  }

  private async updateWorkQueueQuantitiesInternalAsync(items: MenuQuantityItemEnum[] | MenuQuantityItemEnum) {
      if (!items)
      return;

    if (!(items instanceof Array)) {
      items = [items]; // this.getGroupMenuQuantityItemsByMenuQuantityItem(items);
    }

    if (!items.length)
      return;

    const viewModels: MenuWorkQueueSubMenuViewModel[] = [];
    const workQueueItems = await this.getWorkQueueItemQuantityAsync();

    viewModels.push(...workQueueItems);

    this._workQueueQuantifiersSubject.next(viewModels);


   }

  private getGroupMenuQuantityItemsByMenuQuantityItem(menuQuantityItemEnum: MenuQuantityItemEnum): MenuQuantityItemEnum[] {
    for (let groupMenuQuantityItem in this.menu) {
      for (let menuQuantityItem in this.menu[groupMenuQuantityItem]) {
        if (this.menu[groupMenuQuantityItem][menuQuantityItem] === menuQuantityItemEnum) {
          let result = [];
          for (let menu in this.menu[groupMenuQuantityItem]) {
            result.push(this.menu[groupMenuQuantityItem][menu]);
          }
          return result;
        }
      }
    }
    return [];
  }

  private getQuantitiesAsync(request: MenuQuantityItemEnum[]): Promise<MenuQuantityItemViewModel[]> {
    var clinicId = this.userClinicService.clinicId;
    return this.httpClient.getJsonAsync<MenuQuantityItemViewModel[]>('menu/quantities', { menuQuantityItems: request, clinicId }, null, false);
  }

  getWorkQueueSubMenuAsync() {
    var clinicId = this.userClinicService.clinicId;
    const currentUser = this.authService.currentUserValue;
    var assignedUser = !(currentUser) ? null : currentUser.id;
    return this.httpClient.getJsonAsync<MenuWorkQueueSubMenuViewModel[]>('menu/workQueueSubMenu', { assignedUser, clinicId }, null, false);
  }

  getWorkQueueItemQuantityAsync() {
    var clinicId = this.userClinicService.clinicId;
    const currentUser = this.authService.currentUserValue;
    var assignedUser = !(currentUser) ? null : currentUser.id;
    return this.httpClient.getJsonAsync<MenuWorkQueueSubMenuViewModel[]>('menu/WorkQueueItemQuantity', { assignedUser, clinicId }, null, false);
  }

}

interface LastMenuItemData {
  lastUpdatedDate: Date;
  viewModel: MenuQuantityItemViewModel;
}
